import { Entity } from "../models"
import { ItemMechanics } from "./item_mechanics"

class Defense {
    static maximumDefenseProbability = 0.8

    static calculateDefenseProbability = (entity: Entity) => {
        const pctOfTotalProtection = Math.min(
            this.maximumDefenseProbability,
            ItemMechanics.protection.computeProtectionPctOfMax(entity.inventory),
        )
        return pctOfTotalProtection
    }
}

export const FormationTypes = ["square", "circle", "random"] as const

export type FormationType = (typeof FormationTypes)[number]

export const SquadStanceTypes = ["defense", "strategic", "aggressive"]

export type SquadStanceType = (typeof SquadStanceTypes)[number]

export interface SquadState {
    stance: SquadStanceType
    follow: boolean
    formation: FormationType
    count: number
}

class Squad {}

/**
 * How a fighting npc's walking speed is modulated, as multiples of its forged `maxSpeed`.
 *
 * `maxSpeed` is what a spawn is built with and never moves; `speed` is live, and CombatBrain is what
 * moves it -- faster for the duration of an attack, slower while circling a target it is not currently
 * swinging at. Both are written as `maxSpeed * <multiplier>`, which is the property anything reasoning
 * about npc pace depends on.
 *
 * They live down here in mechanics, rather than in the brain that applies them, because they are read
 * from two layers that must not import each other: `npc/brains/combat.ts` applies them, and the ember
 * wave table's approach ceiling has to account for them (`services/ember_wave_approach.ts`) -- a wave
 * whose approach sits just under the player's pace still LUNGES at 1.5x it, so a ceiling that ignored
 * this would only hold while nothing was being attacked.
 */
class NpcSpeed {
    /** While an attack is underway. */
    static attackMultiplier = 1.5
    /** While circling a target rather than closing on it. */
    static circleMultiplier = 0.5
}

export class CombatMechanics {
    static defense = Defense
    static squad = Squad
    static npcSpeed = NpcSpeed
}
